import { Injectable, NgZone } from "@angular/core";
import { BackendService } from "./backend.service";
import { EnvironmentService } from "./environments/environment.service";
import { SessionService } from "./session.service";
import { getHttpErrorStatus, isPermanentHttpClientError, isTransientHttpError } from "./http-error-utils";

// Uploads files straight from the browser to the bucket (Cloudflare R2) with presigned
// multipart URLs from the backend's object storage module (/api/upload). The file never
// goes through the backend, so the upload is not limited by the request limits of the server.
//
// Flow: POST /api/upload (with fileSize, the server decides the part size, every upload is multipart)
// -> PUT every part to its presigned URL -> POST /api/upload/:temporaryFileUploadId/complete.
// The returned temporaryFileUploadId can be passed (once) to the endpoints that accept temporary uploads.
//
// Interrupted uploads can be resumed: the upload id is saved in localStorage and when the same
// file is uploaded again for the same purpose and scope, only the missing parts are uploaded.
// The saved state belongs to the selected backend environment and the logged in admin: the same
// origin can switch between environments (and admins), whose uploads are unknown to each other.

export type DirectBucketUploadRequest = {
  file: File;
  /** Sent as `intendedPurpose`, the backend applies its per purpose policy based on it. */
  intendedPurpose: string;
  /** Narrows the resume state of the file, e.g. the uuid of the entity the file is uploaded to. */
  scopeKey: string;
  /** Defaults to the name of the file. */
  originalFileName?: string;
  contentType?: string;
  onProgress?: (progress: DirectBucketUploadProgress) => void;
  /**
   * Aborting it cancels the upload: in-flight part uploads are aborted, the multipart upload
   * is aborted on the server and the resume state is forgotten. If the upload was completed in the
   * meantime, it is kept, so the next upload of the same file reuses it.
   */
  signal?: AbortSignal;
};

export type DirectBucketUploadProgress = {
  /** Bytes of the finished parts plus the bytes sent so far of the parts in flight. */
  uploadedBytes: number;
  totalBytes: number;
  /** True when a previously interrupted upload of the same file is continued. */
  isResumed: boolean;
};

export type DirectBucketUploadResult = {
  temporaryFileUploadId: string;
  isResumed: boolean;
  /** True when an earlier, already completed upload of the same file was reused (nothing was uploaded now). */
  isReusedCompletedUpload: boolean;
};

export type DirectBucketUploadErrorCode =
  | "CANCELLED"
  | "EMPTY_FILE"
  | "INVALID_SERVER_RESPONSE"
  | "ETAG_NOT_EXPOSED"
  | "PART_UPLOAD_FAILED"
  | "UPLOAD_NO_LONGER_AVAILABLE"
  | "UPLOAD_API_NOT_AVAILABLE"
  | "ALREADY_IN_PROGRESS";

/** Error of the upload itself (not of a backend call). The message is user facing (Hungarian). */
export class DirectBucketUploadError extends Error {
  constructor(
    public readonly code: DirectBucketUploadErrorCode,
    message: string,
    cause?: unknown
  ) {
    super(message, { cause });
    this.name = "DirectBucketUploadError";
  }

  public static isCancellation(error: unknown): boolean {
    return error instanceof DirectBucketUploadError && error.code === "CANCELLED";
  }
}

/** Formats the transferred amount for progress texts, e.g. "14,8 / 40,0 GB". */
export function formatTransferredBytes(uploadedBytes: number, totalBytes: number): string {
  const useGigabytes: boolean = totalBytes >= 1e9;
  const unit: number = useGigabytes ? 1e9 : 1e6;
  const format = (bytes: number): string =>
    (bytes / unit).toLocaleString("hu-HU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  return `${format(uploadedBytes)} / ${format(totalBytes)} ${useGigabytes ? "GB" : "MB"}`;
}

type TemporaryFileUploadStatus = "INITIATED" | "COMPLETED" | "ABORTED" | "FAILED";

type PartUploadUrl = { partNumber: number; uploadUrl: string };

// POST /api/upload
type CreateUploadResponse = {
  temporaryFileUploadId: string;
  // Every part except the last one is exactly this large (bytes), the last one is at most this large
  partSize: number;
  partCount: number;
  parts: Array<PartUploadUrl>;
  expiresAt: string;
};

// POST /api/upload/:temporaryFileUploadId/part-urls
type PartUrlsResponse = { parts: Array<PartUploadUrl>; expiresAt: string };

// GET /api/upload/:temporaryFileUploadId/parts
type UploadPartsResponse = {
  status: TemporaryFileUploadStatus;
  totalParts: number;
  partSize: number;
  fileSize: number;
  expiresAt: string;
  // Only listed while the upload is INITIATED
  uploadedParts: Array<{ partNumber: number; etag: string; size: number }>;
};

// Persisted in localStorage to be able to resume an interrupted upload
type SavedUploadState = {
  temporaryFileUploadId: string;
  partSize: number;
  totalParts: number;
  expiresAt: string;
  completed: boolean;
};

type UploadSession = {
  request: DirectBucketUploadRequest;
  storageKey: string;
  temporaryFileUploadId: string;
  partSize: number;
  totalParts: number;
  expiresAt: string;
  isResumed: boolean;
  partUrls: Map<number, string>;
  etags: Map<number, string>;
  inFlightBytes: Map<number, number>;
  confirmedBytes: number;
  lastReportedBytes: number;
  lastProgressReportAt: number;
  // Aborted when the caller cancels or when a part fails permanently (stops the other workers)
  abortController: AbortController;
  pendingPartUrlRequest: Promise<void> | null;
  failure: unknown;
};

type PartPutFailureKind = "aborted" | "stalled" | "network" | "http" | "missing-etag";

class PartPutFailure extends Error {
  constructor(
    public readonly kind: PartPutFailureKind,
    public readonly status: number = 0,
    public readonly responseText: string = ""
  ) {
    super(`Part upload failed: ${kind} ${status}`);
  }
}

@Injectable({
  providedIn: "root",
})
export class DirectBucketUploadService {
  private static readonly storageKeyPrefix: string = "directBucketUpload:v2:";
  // Keys of the states saved before they were scoped to the environment and the admin, they are removed when found
  private static readonly legacyStorageKeyPrefix: string = "directBucketUpload:v1:";

  private readonly uploadApiUrlFragment: string = "/api/upload";

  private readonly parallelPartUploads: number = 4;
  private readonly maxPartAttempts: number = 8;
  private readonly maxPartUrlRefreshes: number = 3;
  // Idempotent backend calls are retried for about 90 s (1 + 2 + 4 + 8 + 16 + 30 + 30 s), so a deploy of the backend does not stop the upload
  private readonly maxBackendCallAttempts: number = 8;
  private readonly partUrlBatchSize: number = 200;
  // A part upload without any progress for this long is aborted and retried
  private readonly partStallTimeoutMs: number = 2 * 60 * 1000;
  private readonly completeTimeoutMs: number = 10 * 60 * 1000;
  private readonly progressReportIntervalMs: number = 200;
  // A saved upload is resumed while it is valid for at least this long (expiry during the upload is handled)
  private readonly minRemainingValidityMs: number = 60 * 1000;

  private activeUploadCount: number = 0;

  private readonly beforeUnloadListener = (event: BeforeUnloadEvent): void => {
    // Ask the browser to confirm leaving the page while an upload is running
    event.preventDefault();
    event.returnValue = "";
  };

  constructor(
    private backendService: BackendService,
    private environmentService: EnvironmentService,
    private sessionService: SessionService,
    private ngZone: NgZone
  ) {}

  /**
   * Uploads the file to the bucket and returns the temporaryFileUploadId of the completed upload.
   * Resumes the previous upload of the same file (same purpose, scope, name, size and modification time) if possible.
   *
   * Rejects with a DirectBucketUploadError (code CANCELLED when the signal was aborted) or with the
   * error of the failed backend call. The resume state is kept when the upload can be continued later.
   */
  public async upload(request: DirectBucketUploadRequest): Promise<DirectBucketUploadResult> {
    if (!(request.file.size > 0)) {
      throw new DirectBucketUploadError("EMPTY_FILE", "A kiválasztott fájl üres.");
    }

    this.removeExpiredSavedStates();
    const storageKey: string = this.getStorageKey(request);

    // Two tabs must not work on (and cancel) the same saved upload at the same time
    const lockManager: LockManager | undefined = navigator.locks;
    if (lockManager == null) {
      return await this.uploadWithStorageKey(request, storageKey);
    }

    return await lockManager.request(storageKey, { ifAvailable: true }, async (lock: Lock | null) => {
      if (lock == null) {
        throw new DirectBucketUploadError(
          "ALREADY_IN_PROGRESS",
          "Ennek a fájlnak a feltöltése már folyamatban van egy másik böngészőlapon."
        );
      }
      // The lock callback is not run in the Angular zone
      return await this.ngZone.run(() => this.uploadWithStorageKey(request, storageKey));
    });
  }

  private async uploadWithStorageKey(request: DirectBucketUploadRequest, storageKey: string): Promise<DirectBucketUploadResult> {
    let temporaryFileUploadId: string | null = null;
    // A completed upload is kept even if it is cancelled meanwhile: the next attempt reuses it
    let isCompleted: boolean = false;
    this.onUploadStarted();

    try {
      this.throwIfCancelled(request.signal);

      const savedState: SavedUploadState | null = this.readSavedState(storageKey);
      temporaryFileUploadId = savedState?.temporaryFileUploadId ?? null;
      const resumable = await this.findResumableUpload(request, storageKey, savedState);
      temporaryFileUploadId = resumable?.savedState.temporaryFileUploadId ?? null;
      isCompleted = resumable?.listing.status === "COMPLETED";
      this.throwIfCancelled(request.signal);

      if (resumable?.listing.status === "COMPLETED") {
        // Uploaded earlier, but it was not saved by a domain endpoint (rejected, cancelled or the job failed): reuse it
        const totalBytes: number = request.file.size;
        request.onProgress?.({ uploadedBytes: totalBytes, totalBytes: totalBytes, isResumed: true });
        return {
          temporaryFileUploadId: resumable.savedState.temporaryFileUploadId,
          isResumed: true,
          isReusedCompletedUpload: true,
        };
      }

      const session: UploadSession = resumable
        ? this.createResumedSession(request, storageKey, resumable.savedState, resumable.listing)
        : await this.createNewSession(request, storageKey);
      temporaryFileUploadId = session.temporaryFileUploadId;
      this.throwIfCancelled(request.signal);

      await this.uploadMissingParts(session);
      this.throwIfCancelled(request.signal);

      await this.completeUpload(session);
      isCompleted = true;
      this.throwIfCancelled(request.signal);

      return {
        temporaryFileUploadId: session.temporaryFileUploadId,
        isResumed: session.isResumed,
        isReusedCompletedUpload: false,
      };
    } catch (error: any) {
      if (request.signal?.aborted) {
        if (!isCompleted) {
          await this.discardUpload(temporaryFileUploadId, storageKey);
        }
        throw new DirectBucketUploadError("CANCELLED", "A feltöltés megszakítva.", error);
      }

      throw error;
    } finally {
      this.onUploadFinished();
    }
  }

  /**
   * Forgets the resume state of the file. Call it once the temporary upload was used up for good
   * (e.g. a domain endpoint saved it), so the next upload of the same file starts a new upload.
   */
  public forgetSavedUpload(request: Pick<DirectBucketUploadRequest, "file" | "intendedPurpose" | "scopeKey">): void {
    this.removeSavedState(this.getStorageKey(request));
  }

  // ---------------------------------------------------------------------------
  // Resume

  private async findResumableUpload(
    request: DirectBucketUploadRequest,
    storageKey: string,
    savedState: SavedUploadState | null
  ): Promise<{ savedState: SavedUploadState; listing: UploadPartsResponse } | null> {
    if (savedState == null) {
      return null;
    }

    if (!this.isValidFor(savedState.expiresAt, this.minRemainingValidityMs)) {
      this.removeSavedState(storageKey);
      return null;
    }

    let listing: UploadPartsResponse;
    try {
      listing = await this.callBackendWithRetry(
        () => this.backendService.callApi<Object, UploadPartsResponse>(
          this.getUploadApiUrl(savedState.temporaryFileUploadId, "parts"),
          "GET"
        ),
        request.signal
      );
    } catch (error: any) {
      // Unknown, foreign, deleted or not multipart upload: start a new one. Other errors (network, server)
      // are thrown, so the saved upload is not thrown away because of a temporary problem.
      if (isPermanentHttpClientError(error)) {
        this.removeSavedState(storageKey);
        return null;
      }
      throw error;
    }

    const fileSize: number = request.file.size;
    const partSize: number = listing.partSize;
    const isSameFile: boolean = listing.fileSize === fileSize;

    if (
      listing.status === "COMPLETED" &&
      isSameFile &&
      this.isValidFor(listing.expiresAt, this.minRemainingValidityMs)
    ) {
      return { savedState, listing };
    }

    if (
      listing.status === "INITIATED" &&
      isSameFile &&
      Number.isInteger(partSize) &&
      partSize > 0 &&
      listing.totalParts === Math.ceil(fileSize / partSize) &&
      this.isValidFor(listing.expiresAt, this.minRemainingValidityMs)
    ) {
      const updatedState: SavedUploadState = {
        ...savedState,
        partSize: partSize,
        totalParts: listing.totalParts,
        expiresAt: listing.expiresAt,
      };
      this.writeSavedState(storageKey, updatedState);
      return { savedState: updatedState, listing };
    }

    // Aborted, failed, about to expire or does not match the file
    this.removeSavedState(storageKey);
    if (listing.status === "INITIATED") {
      this.abortOnServerQuietly(savedState.temporaryFileUploadId);
    }
    return null;
  }

  private createResumedSession(
    request: DirectBucketUploadRequest,
    storageKey: string,
    savedState: SavedUploadState,
    listing: UploadPartsResponse
  ): UploadSession {
    const session: UploadSession = this.createSession(request, storageKey, savedState, true);

    for (const uploadedPart of listing.uploadedParts ?? []) {
      const isKnownPart: boolean = uploadedPart.partNumber >= 1 && uploadedPart.partNumber <= session.totalParts;
      // A part with an unexpected size is uploaded again (the new upload replaces it)
      if (isKnownPart && uploadedPart.etag && uploadedPart.size === this.getPartByteSize(session, uploadedPart.partNumber)) {
        session.etags.set(uploadedPart.partNumber, uploadedPart.etag);
        session.confirmedBytes += uploadedPart.size;
      }
    }

    return session;
  }

  // ---------------------------------------------------------------------------
  // New upload

  private async createNewSession(request: DirectBucketUploadRequest, storageKey: string): Promise<UploadSession> {
    const fileSize: number = request.file.size;
    let response: CreateUploadResponse;
    try {
      response = await this.backendService.callApi<Object, CreateUploadResponse>(
        this.uploadApiUrlFragment,
        "POST",
        {
          originalFileName: request.originalFileName ?? request.file.name,
          contentType: request.contentType,
          intendedPurpose: request.intendedPurpose,
          fileSize: fileSize,
        }
      );
    } catch (error: any) {
      // The endpoint has no 404 response: the backend of the selected environment does not have the upload API yet
      if (getHttpErrorStatus(error) === 404) {
        throw new DirectBucketUploadError(
          "UPLOAD_API_NOT_AVAILABLE",
          "A kiválasztott környezet szerverén még nincs fent a közvetlen feltöltést támogató verzió.",
          error
        );
      }
      throw error;
    }

    const partSize: number | undefined = response?.partSize;
    const isValidResponse: boolean =
      typeof response?.temporaryFileUploadId === "string" &&
      Number.isInteger(partSize) &&
      partSize > 0 &&
      response.partCount === Math.ceil(fileSize / partSize);

    if (!isValidResponse) {
      if (response?.temporaryFileUploadId) {
        this.abortOnServerQuietly(response.temporaryFileUploadId);
      }
      throw new DirectBucketUploadError(
        "INVALID_SERVER_RESPONSE",
        "A szerver nem a várt módon indította el a feltöltést (hiányzó vagy hibás részméret)."
      );
    }

    const savedState: SavedUploadState = {
      temporaryFileUploadId: response.temporaryFileUploadId,
      partSize: partSize,
      totalParts: response.partCount,
      expiresAt: response.expiresAt,
      completed: false,
    };
    this.writeSavedState(storageKey, savedState);

    const session: UploadSession = this.createSession(request, storageKey, savedState, false);
    for (const part of response.parts ?? []) {
      session.partUrls.set(part.partNumber, part.uploadUrl);
    }

    return session;
  }

  private createSession(
    request: DirectBucketUploadRequest,
    storageKey: string,
    savedState: SavedUploadState,
    isResumed: boolean
  ): UploadSession {
    return {
      request: request,
      storageKey: storageKey,
      temporaryFileUploadId: savedState.temporaryFileUploadId,
      partSize: savedState.partSize,
      totalParts: savedState.totalParts,
      expiresAt: savedState.expiresAt,
      isResumed: isResumed,
      partUrls: new Map<number, string>(),
      etags: new Map<number, string>(),
      inFlightBytes: new Map<number, number>(),
      confirmedBytes: 0,
      lastReportedBytes: 0,
      lastProgressReportAt: 0,
      abortController: new AbortController(),
      pendingPartUrlRequest: null,
      failure: null,
    };
  }

  // ---------------------------------------------------------------------------
  // Parts

  private async uploadMissingParts(session: UploadSession): Promise<void> {
    const queue: Array<number> = [];
    for (let partNumber = 1; partNumber <= session.totalParts; partNumber++) {
      if (!session.etags.has(partNumber)) {
        queue.push(partNumber);
      }
    }

    this.reportProgress(session, true);

    const externalSignal: AbortSignal | undefined = session.request.signal;
    const onCancel = (): void => session.abortController.abort();
    externalSignal?.addEventListener("abort", onCancel);

    try {
      const workerCount: number = Math.min(this.parallelPartUploads, queue.length);
      const workers: Array<Promise<void>> = [];
      for (let i = 0; i < workerCount; i++) {
        workers.push(this.runPartUploadWorker(session, queue));
      }
      await Promise.all(workers);
    } finally {
      externalSignal?.removeEventListener("abort", onCancel);
    }

    this.throwIfCancelled(externalSignal);
    if (session.failure) {
      throw session.failure;
    }
  }

  private async runPartUploadWorker(session: UploadSession, queue: Array<number>): Promise<void> {
    while (!session.abortController.signal.aborted) {
      const partNumber: number | undefined = queue.shift();
      if (partNumber === undefined) {
        return;
      }

      try {
        const etag: string = await this.uploadPart(session, partNumber, queue);
        session.etags.set(partNumber, etag);
        session.inFlightBytes.delete(partNumber);
        session.confirmedBytes += this.getPartByteSize(session, partNumber);
        this.reportProgress(session, true);
      } catch (error: any) {
        // The first failure stops every worker (and aborts their in-flight requests)
        session.failure ??= error;
        session.abortController.abort();
        return;
      }
    }
  }

  // Uploads one part with retries, returns its ETag
  private async uploadPart(session: UploadSession, partNumber: number, queue: ReadonlyArray<number>): Promise<string> {
    let failedAttempts: number = 0;
    let urlRefreshes: number = 0;

    while (true) {
      this.throwIfCancelled(session.abortController.signal);
      const uploadUrl: string = await this.getPartUploadUrl(session, partNumber, queue);

      try {
        return await this.putPart(session, partNumber, uploadUrl);
      } catch (error: any) {
        if (!(error instanceof PartPutFailure)) {
          throw error;
        }

        session.inFlightBytes.delete(partNumber);

        switch (error.kind) {
          case "aborted":
            throw new DirectBucketUploadError("CANCELLED", "A feltöltés megszakítva.", error);

          case "missing-etag":
            throw new DirectBucketUploadError(
              "ETAG_NOT_EXPOSED",
              "A tárhely nem adta vissza a feltöltött rész ETag fejlécét. A bucket CORS beállításában engedélyezni kell az ETag fejléc kiolvasását (ExposeHeaders: ETag).",
              error
            );
        }

        // Expired or otherwise rejected URL: get a new one and try again
        const isUrlRejected: boolean =
          error.kind === "http" && (error.status === 403 || /expired/i.test(error.responseText));
        if (isUrlRejected && urlRefreshes < this.maxPartUrlRefreshes) {
          urlRefreshes++;
          session.partUrls.delete(partNumber);
          continue;
        }

        if (error.kind === "http" && error.status === 404) {
          // NoSuchUpload: the multipart upload was aborted or expired on the bucket
          this.removeSavedState(session.storageKey);
          throw new DirectBucketUploadError(
            "UPLOAD_NO_LONGER_AVAILABLE",
            "A félbehagyott feltöltés már nem létezik a tárhelyen, ezért a feltöltést elölről kell kezdeni.",
            error
          );
        }

        const isRetryable: boolean =
          error.kind === "network" ||
          error.kind === "stalled" ||
          (error.kind === "http" && (error.status === 408 || error.status === 429 || error.status >= 500));
        failedAttempts++;
        if (isRetryable && failedAttempts < this.maxPartAttempts) {
          await this.sleep(this.getBackoffDelayMs(failedAttempts), session.abortController.signal);
          continue;
        }

        throw new DirectBucketUploadError("PART_UPLOAD_FAILED", this.describePartFailure(partNumber, error), error);
      }
    }
  }

  private describePartFailure(partNumber: number, failure: PartPutFailure): string {
    switch (failure.kind) {
      case "network":
        return `A(z) ${partNumber}. rész feltöltése többszöri próbálkozás után sem sikerült (hálózati hiba, vagy a tárhely CORS beállítása nem engedi a feltöltést).`;
      case "stalled":
        return `A(z) ${partNumber}. rész feltöltése többszöri próbálkozás után is elakadt.`;
      default:
        return `A tárhely elutasította a(z) ${partNumber}. rész feltöltését (HTTP ${failure.status}).`;
    }
  }

  private async getPartUploadUrl(session: UploadSession, partNumber: number, queue: ReadonlyArray<number>): Promise<string> {
    while (true) {
      const cachedUrl: string | undefined = session.partUrls.get(partNumber);
      if (cachedUrl) {
        return cachedUrl;
      }

      // Several workers may need URLs at the same time: wait for the running request,
      // it may contain this part as well
      if (session.pendingPartUrlRequest != null) {
        await session.pendingPartUrlRequest;
        continue;
      }

      // Request this part together with the upcoming parts that have no URL yet
      const partNumbers: Array<number> = [partNumber];
      for (const queuedPartNumber of queue) {
        if (partNumbers.length >= this.partUrlBatchSize) {
          break;
        }
        if (queuedPartNumber !== partNumber && !session.partUrls.has(queuedPartNumber)) {
          partNumbers.push(queuedPartNumber);
        }
      }

      const request: Promise<void> = this.fetchPartUploadUrls(session, partNumbers).finally(() => {
        session.pendingPartUrlRequest = null;
      });
      session.pendingPartUrlRequest = request;
      await request;

      const url: string | undefined = session.partUrls.get(partNumber);
      if (url) {
        return url;
      }

      throw new DirectBucketUploadError(
        "INVALID_SERVER_RESPONSE",
        `A szerver nem adott feltöltési címet a(z) ${partNumber}. részhez.`
      );
    }
  }

  private async fetchPartUploadUrls(session: UploadSession, partNumbers: Array<number>): Promise<void> {
    let response: PartUrlsResponse;
    try {
      response = await this.callBackendWithRetry(
        () => this.backendService.callApi<Object, PartUrlsResponse>(
          this.getUploadApiUrl(session.temporaryFileUploadId, "part-urls"),
          "POST",
          { partNumbers: partNumbers }
        ),
        session.abortController.signal
      );
    } catch (error: any) {
      if (isPermanentHttpClientError(error)) {
        // The upload is not in progress any more (expired, aborted, failed) or not ours
        this.removeSavedState(session.storageKey);
        throw new DirectBucketUploadError(
          "UPLOAD_NO_LONGER_AVAILABLE",
          "A félbehagyott feltöltés már nem folytatható, ezért a feltöltést elölről kell kezdeni.",
          error
        );
      }
      throw error;
    }

    for (const part of response?.parts ?? []) {
      session.partUrls.set(part.partNumber, part.uploadUrl);
    }
  }

  // PUTs one part straight to the presigned URL with a raw XHR (no auth headers, no interceptors),
  // resolves with the ETag of the part
  private putPart(session: UploadSession, partNumber: number, uploadUrl: string): Promise<string> {
    const start: number = (partNumber - 1) * session.partSize;
    const end: number = Math.min(start + session.partSize, session.request.file.size);
    // No content type: the presigned UploadPart URL does not sign one
    const body: Blob = session.request.file.slice(start, end);
    const abortSignal: AbortSignal = session.abortController.signal;

    return new Promise<string>((resolve, reject) => {
      // Progress events would trigger change detection very often, they are reported throttled instead
      this.ngZone.runOutsideAngular(() => {
        if (abortSignal.aborted) {
          reject(new PartPutFailure("aborted"));
          return;
        }

        const xhr: XMLHttpRequest = new XMLHttpRequest();
        let stallTimer: ReturnType<typeof setTimeout> | null = null;
        let isStalled: boolean = false;

        const onAbortSignal = (): void => xhr.abort();
        const restartStallTimer = (): void => {
          if (stallTimer !== null) {
            clearTimeout(stallTimer);
          }
          stallTimer = setTimeout(() => {
            isStalled = true;
            xhr.abort();
          }, this.partStallTimeoutMs);
        };
        const cleanUp = (): void => {
          if (stallTimer !== null) {
            clearTimeout(stallTimer);
          }
          abortSignal.removeEventListener("abort", onAbortSignal);
        };

        xhr.upload.onprogress = (event: ProgressEvent): void => {
          restartStallTimer();
          session.inFlightBytes.set(partNumber, event.loaded);
          this.reportProgress(session, false);
        };

        xhr.onload = (): void => {
          cleanUp();
          if (xhr.status < 200 || xhr.status >= 300) {
            reject(new PartPutFailure("http", xhr.status, xhr.responseText ?? ""));
            return;
          }

          const etag: string | null = xhr.getResponseHeader("ETag");
          if (!etag) {
            reject(new PartPutFailure("missing-etag", xhr.status));
            return;
          }

          resolve(etag);
        };
        xhr.onerror = (): void => {
          cleanUp();
          reject(new PartPutFailure("network"));
        };
        xhr.onabort = (): void => {
          cleanUp();
          reject(new PartPutFailure(isStalled ? "stalled" : "aborted"));
        };

        abortSignal.addEventListener("abort", onAbortSignal);
        xhr.open("PUT", uploadUrl, true);
        xhr.send(body);
        restartStallTimer();
      });
    });
  }

  private getPartByteSize(session: UploadSession, partNumber: number): number {
    const start: number = (partNumber - 1) * session.partSize;
    return Math.max(0, Math.min(session.partSize, session.request.file.size - start));
  }

  // ---------------------------------------------------------------------------
  // Complete, abort

  private async completeUpload(session: UploadSession): Promise<void> {
    const parts: Array<{ partNumber: number; etag: string }> = [];
    for (let partNumber = 1; partNumber <= session.totalParts; partNumber++) {
      parts.push({ partNumber: partNumber, etag: session.etags.get(partNumber) });
    }

    try {
      await this.callBackendWithRetry(
        () => this.backendService.callApi(
          this.getUploadApiUrl(session.temporaryFileUploadId, "complete"),
          "POST",
          { parts: parts },
          undefined,
          undefined,
          this.completeTimeoutMs
        ),
        undefined // Completing is not interrupted, it is short and idempotent
      );
    } catch (error: any) {
      if (isPermanentHttpClientError(error)) {
        // E.g. the size of the assembled object does not match: the server marked the upload as failed
        this.removeSavedState(session.storageKey);
      }
      throw error;
    }

    this.writeSavedState(session.storageKey, {
      temporaryFileUploadId: session.temporaryFileUploadId,
      partSize: session.partSize,
      totalParts: session.totalParts,
      expiresAt: session.expiresAt,
      completed: true,
    });
    this.reportProgress(session, true);
  }

  private async discardUpload(temporaryFileUploadId: string | null, storageKey: string): Promise<void> {
    this.removeSavedState(storageKey);
    if (temporaryFileUploadId == null) {
      return;
    }

    try {
      await this.backendService.callApi(this.getUploadApiUrl(temporaryFileUploadId, "abort"), "POST", {});
    } catch (error: any) {
      // Best effort, the server cleans up expired uploads anyway
      console.warn("Could not abort the multipart upload", temporaryFileUploadId, error);
    }
  }

  private abortOnServerQuietly(temporaryFileUploadId: string): void {
    this.backendService
      .callApi(this.getUploadApiUrl(temporaryFileUploadId, "abort"), "POST", {})
      .catch((error: any) => console.warn("Could not abort the multipart upload", temporaryFileUploadId, error));
  }

  private getUploadApiUrl(temporaryFileUploadId: string, action: "parts" | "part-urls" | "complete" | "abort"): string {
    return `${this.uploadApiUrlFragment}/${encodeURIComponent(temporaryFileUploadId)}/${action}`;
  }

  // ---------------------------------------------------------------------------
  // Helpers

  private reportProgress(session: UploadSession, force: boolean): void {
    const onProgress = session.request.onProgress;
    if (!onProgress) {
      return;
    }

    const now: number = performance.now();
    if (!force && now - session.lastProgressReportAt < this.progressReportIntervalMs) {
      return;
    }
    session.lastProgressReportAt = now;

    let inFlightBytes: number = 0;
    for (const bytes of session.inFlightBytes.values()) {
      inFlightBytes += bytes;
    }

    const totalBytes: number = session.request.file.size;
    // Never goes backwards, even if an in-flight part is retried
    const uploadedBytes: number = Math.min(
      totalBytes,
      Math.max(session.lastReportedBytes, session.confirmedBytes + inFlightBytes)
    );
    session.lastReportedBytes = uploadedBytes;

    this.ngZone.run(() => onProgress({ uploadedBytes, totalBytes, isResumed: session.isResumed }));
  }

  // Retries idempotent backend calls on transient errors (network, deploys, gateway errors)
  private async callBackendWithRetry<T>(call: () => Promise<T>, signal: AbortSignal | undefined): Promise<T> {
    for (let attempt = 1; ; attempt++) {
      try {
        return await call();
      } catch (error: any) {
        if (attempt >= this.maxBackendCallAttempts || !isTransientHttpError(error) || signal?.aborted) {
          throw error;
        }
        await this.sleep(this.getBackoffDelayMs(attempt), signal);
      }
    }
  }

  private getBackoffDelayMs(attempt: number): number {
    const delay: number = Math.min(30000, 1000 * 2 ** (attempt - 1));
    return Math.round(delay * (0.75 + Math.random() * 0.5));
  }

  private sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DirectBucketUploadError("CANCELLED", "A feltöltés megszakítva."));
        return;
      }

      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DirectBucketUploadError("CANCELLED", "A feltöltés megszakítva."));
      };
      const timer = setTimeout(() => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }, ms);
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  }

  private throwIfCancelled(signal: AbortSignal | undefined): void {
    if (signal?.aborted) {
      throw new DirectBucketUploadError("CANCELLED", "A feltöltés megszakítva.");
    }
  }

  private isValidFor(expiresAt: string, minRemainingMs: number): boolean {
    const expiresAtMs: number = Date.parse(expiresAt);
    return !Number.isNaN(expiresAtMs) && expiresAtMs - Date.now() > minRemainingMs;
  }

  private onUploadStarted(): void {
    if (this.activeUploadCount++ === 0) {
      window.addEventListener("beforeunload", this.beforeUnloadListener);
    }
  }

  private onUploadFinished(): void {
    if (--this.activeUploadCount === 0) {
      window.removeEventListener("beforeunload", this.beforeUnloadListener);
    }
  }

  // ---------------------------------------------------------------------------
  // Resume state (localStorage)

  // Also the name of the lock that keeps two tabs from working on the same saved upload
  private getStorageKey(request: Pick<DirectBucketUploadRequest, "file" | "intendedPurpose" | "scopeKey">): string {
    const { file } = request;
    return DirectBucketUploadService.storageKeyPrefix + JSON.stringify([
      this.environmentService.getCurrentEnvironment()?.serverHost ?? "",
      this.sessionService.getUuid() ?? "",
      request.intendedPurpose,
      request.scopeKey,
      file.name,
      file.size,
      file.lastModified,
    ]);
  }

  private readSavedState(storageKey: string): SavedUploadState | null {
    try {
      const rawState: string | null = localStorage.getItem(storageKey);
      if (rawState == null) {
        return null;
      }

      const state: Partial<SavedUploadState> = JSON.parse(rawState);
      const isValidState: boolean =
        typeof state?.temporaryFileUploadId === "string" &&
        Number.isInteger(state.partSize) &&
        state.partSize > 0 &&
        Number.isInteger(state.totalParts) &&
        state.totalParts > 0 &&
        typeof state.expiresAt === "string";

      if (!isValidState) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return state as SavedUploadState;
    } catch (error: any) {
      return null;
    }
  }

  private writeSavedState(storageKey: string, state: SavedUploadState): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error: any) {
      // Storage is full or disabled: the upload works, it just cannot be resumed
      console.warn("Could not save the upload state", error);
    }
  }

  // States of abandoned uploads would stay in the storage forever otherwise. The expired states of every
  // environment and admin are removed (they are of no use anywhere), and so are the unscoped legacy states.
  private removeExpiredSavedStates(): void {
    try {
      const keysToRemove: Array<string> = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key: string | null = localStorage.key(i);
        if (key?.startsWith(DirectBucketUploadService.legacyStorageKeyPrefix)) {
          keysToRemove.push(key);
        } else if (key?.startsWith(DirectBucketUploadService.storageKeyPrefix)) {
          const state: SavedUploadState | null = this.readSavedState(key);
          if (state == null || !this.isValidFor(state.expiresAt, 0)) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach((key: string) => localStorage.removeItem(key));
    } catch (error: any) {
      console.warn("Could not clean up the saved upload states", error);
    }
  }

  private removeSavedState(storageKey: string): void {
    try {
      localStorage.removeItem(storageKey);
    } catch (error: any) {
      console.warn("Could not remove the upload state", error);
    }
  }
}
